import React, { useCallback, useEffect, useRef, useLayoutEffect, useState, memo } from "react"
import { Layout, List } from "antd"
import { useDispatch, useSelector } from "react-redux"
import { useSearchParams } from "react-router-dom"
import { useDevice } from "hooks/useDevice"
import { Message } from "../Message"
import { HistoryHeader } from "../HistoryHeader"
import { MessageForm } from "./MessageForm"
import { debounce } from "lodash"
import PropTypes from "prop-types"
import {
  activeConversationSelector,
  getConversation,
  messagesMetadataSelector,
  messagesSelector,
  getConversations,
  clearTriggeredByWS
} from "store/conversations"
import { collectionAnySelector, collectionSelector } from "store/selectors"
import { groupMessagesByDateAndAuthor } from "../utils"
import styles from "./ChatHistory.module.css"
import classNames from "classnames"
import Spinner from "components/common/Spinner"

const { Content } = Layout

const History = ({ selectedConversationId, subscription, setSubscription, guideClientId, moduleName, isShowHeader = true,isClient }) => {
  const dispatch = useDispatch()
  const [setSearchParams] = useSearchParams()
  const [cachedConversation, setCachedConversation] = useState(null)
  const [loading, setLoading] = useState(false)
  const { isMobile, isTablet } = useDevice()

  const scrollWrapperRef = useRef(null)
  const scrollAnchorRef = useRef(null)
  const topVisibleMessageIdRef = useRef(null)

  const isFetchingRef = useRef(false)
  const fetchedPagesRef = useRef(new Set())
  const firstRenderRef = useRef(true)
  const loadingOlderRef = useRef(false)

  const hasConversations = useSelector(collectionAnySelector("conversations"))
  const conversations = useSelector(collectionSelector("conversations"))
  const triggeredByWS = useSelector((state) => state.conversations.triggeredByWS)
  const messages = useSelector(messagesSelector)
  const metadata = useSelector(messagesMetadataSelector)
  const activeConversation = useSelector(activeConversationSelector)
  const user = useSelector((state) => state.user)
  const isGuide = user.role === "guide_user"
  useEffect(() => {
    if (activeConversation?.id) {
      setCachedConversation(activeConversation)
      fetchedPagesRef.current.clear()
      loadingOlderRef.current = false
    }
  }, [activeConversation])
  
  const conversationToUse = activeConversation?.id ? activeConversation : cachedConversation
  const participant = isGuide || !isClient ? conversationToUse?.client : conversationToUse?.contact

  const scrollToBottom = (behavior = "auto") => {
    scrollAnchorRef.current?.scrollIntoView({ behavior })
  }

  useEffect(() => {
    if (triggeredByWS) {
      scrollToBottom("smooth")
      dispatch(clearTriggeredByWS())
    }
  }, [triggeredByWS, dispatch])

  const fetchMessages = useCallback(
    (nextPage = false) => {
      if (!selectedConversationId) return Promise.resolve()
      const scrollContainer = scrollWrapperRef.current

      if (nextPage) {
        const firstVisible = scrollContainer?.querySelector("[data-message-id]")
        topVisibleMessageIdRef.current = firstVisible?.getAttribute("data-message-id")
        loadingOlderRef.current = true
      }
      isFetchingRef.current = true

      return dispatch(getConversation(selectedConversationId, nextPage))
        .then(() => {
          setLoading(false)
          if (!nextPage) {
            scrollToBottom("auto")
          }
        })
        .catch(() => {
          setSearchParams({})
          setLoading(false)
        })
        .finally(() => {
          firstRenderRef.current = false
          isFetchingRef.current = false
          loadingOlderRef.current = false
        })
    },
    [selectedConversationId, dispatch, setSearchParams]
  )

  useLayoutEffect(() => {
    const scrollContainer = scrollWrapperRef.current
    if (loadingOlderRef.current && topVisibleMessageIdRef.current && scrollContainer) {
      const el = scrollContainer.querySelector(`[data-message-id="${topVisibleMessageIdRef.current}"]`)
      if (el) {
        el.scrollIntoView({ block: "center" })
      }
      topVisibleMessageIdRef.current = null
    } else if (firstRenderRef.current && messages.length > 0) {
      scrollToBottom("auto")
      firstRenderRef.current = false
    }
  }, [messages])

  useEffect(() => {
    if (!selectedConversationId) return
    const hasSelected = conversations.find((conv) => conv.id === selectedConversationId) 
    if (hasSelected) {
      setLoading(true)
      fetchMessages(false)
    } else if (hasConversations && !hasSelected) {
      dispatch(getConversations(null)).then(() => {
        setLoading(true)
        fetchMessages(false)
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversationId, hasConversations, fetchMessages, dispatch])

  const groupedMessages = groupMessagesByDateAndAuthor(messages)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleScroll = useCallback(
    debounce((e) => {
      const container = e.target
      if (container.scrollTop <= 50 && !isFetchingRef.current) {
        const nextPage = metadata?.next_page
        if (nextPage && !fetchedPagesRef.current.has(nextPage)) {
          fetchedPagesRef.current.add(nextPage)
          fetchMessages(true)
        }
      }
    }, 200),
    [metadata, fetchMessages]
  )

  return (
    <>
      {loading ? (
        <Spinner className="flex-fill" />
      ) : (
        <div className={`${moduleName}  ${classNames(styles.history, (isMobile || isTablet) && styles.smallScreen)}`}>

          {isShowHeader &&
            <HistoryHeader participant={participant} guideClientId={guideClientId} isMobile={isMobile} isTablet={isTablet} isClient={isClient} />}
          <Content className={styles.content}>
            <div className={styles.scrollWrapper} ref={scrollWrapperRef} onScroll={handleScroll}>
              <List
                dataSource={groupedMessages}
                renderItem={(msg) => (
                  <Message
                    key={msg.id}
                    message={msg}
                    data-message-id={msg.id}
                    isOwnMessage={user?.id === msg?.author?.id}
                    isMobile={isMobile}
                    user={conversationToUse?.contact_id}
                    conversation={conversationToUse}
                    subscription={subscription}
                    setSubscription={setSubscription}
                  />
                )}
              />
              <div ref={scrollAnchorRef} />
            </div>
          </Content>
          <MessageForm conversation={conversationToUse} isSmallScreen={isMobile || isTablet} subscription={subscription} />
        </div>
      )}
    </>
  )
}

History.propTypes = {
  selectedConversationId: PropTypes.number.isRequired,
  subscription: PropTypes.object.isRequired,
  setSubscription: PropTypes.func.isRequired,
  guideClientId: PropTypes.string,
  moduleName: PropTypes.string,
  isShowHeader: PropTypes.bool,
  isClient: PropTypes.bool
}

export const ChatHistory = memo(History)
